import { cn } from '../../utils/cn'

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

export function LoadingSpinner({ size = 'md', className }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-8 h-8 border-2',
    lg: 'w-12 h-12 border-4',
    xl: 'w-16 h-16 border-4',
  }

  return (
    <div
      className={cn(
        'inline-block animate-spin rounded-full border-solid border-blue-600 border-t-transparent',
        sizeClasses[size],
        className
      )}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  )
}

interface LoadingOverlayProps {
  visible: boolean
  message?: string
  className?: string
}

export function LoadingOverlay({ visible, message, className }: LoadingOverlayProps) {
  if (!visible) return null

  return (
    <div className={cn('fixed inset-0 bg-black/50 flex items-center justify-center z-50', className)}>
      <div className="bg-white rounded-lg p-6 shadow-xl">
        <div className="flex items-center space-x-4">
          <LoadingSpinner size="lg" />
          {message && <p className="text-gray-700 font-medium">{message}</p>}
        </div>
      </div>
    </div>
  )
}

interface LoadingStateProps {
  loading: boolean
  error?: string | null
  children: React.ReactNode
  fallback?: React.ReactNode
}

export function LoadingState({ loading, error, children, fallback }: LoadingStateProps) {
  if (loading) {
    return (
      fallback || (
        <div className="flex items-center justify-center p-8">
          <LoadingSpinner size="lg" />
        </div>
      )
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-2">⚠️</div>
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default LoadingSpinner
